<script setup lang="ts">
import {
  formatting,
  LegacyDelivery,
  LegacyInventoryItem,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyOrderRow,
  useLocalization
} from '@hantera/portal-app';
import {DecimalInput} from '@hantera/design-system';
import {computed, inject, Ref, ref, watch} from 'vue';
import {Decimal} from 'decimal.js';
import useBackend from '@/composition/useBackend';
import {NewRowAdjustment, newRowAdjustmentKey, orderRulesKey} from '../order-keys';
import {EditableOrderRule} from '../editable-order-adjustment';

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const allRules = inject(orderRulesKey, <Ref<EditableOrderRule[]>><any>[]);
const newRowAdjustment = inject(newRowAdjustmentKey, <Ref<NewRowAdjustment>><any>{});

const props = defineProps<{
  row: LegacyOrderRow
  delivery: LegacyDelivery
}>()

const backend = useBackend();
const { number, currency } = formatting;
const t = useLocalization();
const t2 = useLocalization('components.order.orderLine.details');

const inputQuantity = ref<Decimal>(props.row.quantity);
const qtyInput = ref<typeof DecimalInput>();
const inventoryItems = ref<LegacyInventoryItem[]>();

watch(() => props.row.quantity, () => {
  inputQuantity.value = props.row.quantity;
})

const state = computed(() => {
  if (props.delivery.state === 'Open') {
    return 'open';
  } else if (props.delivery.state === 'Picking') {
    return 'processing';
  }
  return 'shipped';
});

const listingUnitPrice = computed(() => {
  return currency(props.row.unitPrice, order.value.currencyCode);
});

const totalAdjustment = computed(() => {
  let result = allRules.value.reduce((res, rule) => {
    return res.add(rule.affectedRows.reduce((res2, adj) => {
      if (adj.rowId !== props.row.rowId || !adj.isRowBased) {
        return res2;
      }
      return res2.add(adj.value);
    }, new Decimal(0)));
  }, new Decimal(0));
  
  if (result.eq(0)) return new Decimal(0)
  
  return result.mul(-1);
});

const adjustmentPercent = computed(() => {
  
  const denominator = rowTotal.value.add(totalAdjustment.value)
  
  if (denominator.eq(0)) {
    return new Decimal(0)
  }
  
  return totalAdjustment.value.div(denominator)
});

const totalUnitAdjustment = computed(() => {
  return currency(totalAdjustment.value.dividedBy(qty.value), order.value.currencyCode);
});

const availableStock = computed(() => {
  // TODO Take delivery's inventory date into account (only count available up to inventory date)
  return props.row.stock?.filter(r => r.inventoryId === props.delivery.inventoryId).reduce((res, i) => {
    return res.add(i.availablePhysicalStock);
  }, new Decimal(0)).add(props.row.reservedQuantity);
})

const pendingReturnQuantity = computed(() => {
  return order.value.rmas
    .filter(r => r.state !== 'Completed')
    .flatMap(r => r.rows)
    .filter(r => r.orderRow.rowId === props.row.rowId)
    .reduce((res, row) => {
      return res.add(row.quantity);
    }, new Decimal(0));
});

const qty = computed(() => {
  return props.row.quantity.sub(props.row.returnedQuantity).sub(props.row.lostQuantity);
});

const unitAdjustment = computed(() => {
  let result = allRules.value.reduce((res, rule) => {
    return res.add(rule.affectedRows.reduce((res2, adj) => {
      if (adj.rowId !== props.row.rowId) {
        return res2;
      }
      return res2.add(adj.value);
    }, new Decimal(0)));
  }, new Decimal(0)).dividedBy(qty.value);
  
  return result;
});

const sellingUnitPrice = computed(() => {
  return qty.value.gt(0) ? props.row.unitPrice.add(unitAdjustment.value) : new Decimal(0);
});


const rowTotal = computed(() => {

  let nonRowBasedAdjustments = allRules.value.reduce((res, rule) => {
    return res.add(rule.affectedRows.reduce((res2, adj) => {
      if (adj.rowId !== props.row.rowId || adj.isRowBased) {
        return res2;
      }
      return res2.add(adj.value);
    }, new Decimal(0)));
  }, new Decimal(0));

  return props.row.rowTotal.sub(nonRowBasedAdjustments)
});

function setQuantity() {
  try {
    if (!props.row.quantity.eq(inputQuantity.value)) {

      backend.legacyOrders.buildCommandGroup(
        order.value.orderId,
        '{articleNumber} quantity from {oldQuantity} to {newQuantity}', {
        oldQuantity: props.row.quantity,
        newQuantity: inputQuantity.value,
        articleNumber: props.row.item.articleNumber,
      }).command(legacyOrderCommands.updateRowQuantity, {
        rowId: props.row.rowId,
        newQuantity: inputQuantity.value,
      }).command(legacyOrderCommands.addActivityLog, {
        message: '{articleNumber} quantity from {oldQuantity} to {newQuantity}',
        fields: {
          oldQuantity: props.row.quantity,
          newQuantity: inputQuantity.value,
          articleNumber: props.row.item.articleNumber,
        }
      })
        .send();
    }
  } catch {
    qtyInput.value?.setError();
    return;
  }
}

const orderedQty = computed(() => {
  if (props.row.fields['NotOriginallyOnTheOrder'] === 'True') {
    return new Decimal(0)
  }
  
  return props.row.quantity
})

</script>

<template>
  <div
    class="legacy-order-row-summary"
    :class="`legacy-order-row-summary--${state}`"
  >
    <div
      class="legacy-order-row-summary__item"
    >
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.unitPrice') }}
      </div>
      <div class="legacy-order-row-summary__value">
        {{ listingUnitPrice }}
      </div>
    </div>
    
    <div
      class="legacy-order-row-summary__item"
      v-if="state === 'open'"
    >
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.quantity', { unit: 'pcs' }) }}
      </div>
      <div class="legacy-order-row-summary__value">
        <DecimalInput
          v-model="inputQuantity"
          @blur="setQuantity"
          :minValue="0"
          ref="qtyInput"
          :selectOnFocus="true"
        />
      </div>
    </div>

    <div
      class="legacy-order-row-summary__item"
      v-if="state === 'processing' || state === 'shipped'"
    >
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.orderedQuantity') }}
      </div>
      <div class="legacy-order-row-summary__value">
        {{ orderedQty }}
      </div>
    </div>

    <div
      class="legacy-order-row-summary__item"
      v-if="state === 'open' || state === 'processing'"
    >
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.reservedQuantity') }}
      </div>
      <div
        class="legacy-order-row-summary__value"
        :class="{
          'legacy-order-row-summary__value--warning': props.row.reservedQuantity.lt(props.row.quantity) && availableStock?.eq(0),
        }"
      >
        {{ number(props.row.reservedQuantity) }}
      </div>
    </div>

    <div
      class="legacy-order-row-summary__item"
      v-if="state === 'open' || state === 'processing'"
    >
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.availableStockQuantity') }}
      </div>
      <div class="legacy-order-row-summary__value">
        <template v-if="availableStock">{{ number(availableStock) }}</template>
      </div>
    </div>

    <div
      class="legacy-order-row-summary__item"
      v-if="state === 'shipped'"
    >
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.returnedQuantity') }}
      </div>
      <div
        class="legacy-order-row-summary__value"
        :class="{
          'warning': props.row.returnedQuantity.gt(0) || pendingReturnQuantity.gt(0),
        }"
      >
        {{ props.row.returnedQuantity }}
        <span
          v-if="pendingReturnQuantity.gt(0)"
          :title="t('orderView.orderRow.pendingReturnQty', { quantity: number(pendingReturnQuantity) })"
        >({{ number(pendingReturnQuantity) }})</span>
      </div>
    </div>

    <div
      class="legacy-order-row-summary__item"
      v-if="state === 'shipped'"
    >
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.lostQuantity') }}
      </div>
      <div
        class="legacy-order-row-summary__value"
        :class="{
          'warning': props.row.lostQuantity.gt(0),
        }"
      >
        {{ props.row.lostQuantity }}
      </div>
    </div>


    <div class="legacy-order-row-summary__item">
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t2('discount') }}
      </div>
      <div class="legacy-order-row-summary__value">
        {{ currency(totalAdjustment, order.currencyCode) }}
      </div>
    </div>
    
    <div class="legacy-order-row-summary__item">
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        %
      </div>
      <div class="legacy-order-row-summary__value">
        {{formatting.percent(adjustmentPercent)}}
      </div>
    </div>

    <div class="legacy-order-row-summary__item legacy-order-row-summary__item--total">
      <div
        class="legacy-order-row-summary__label"
        data-sentry-unmask
      >
        {{ t('orderRow.rowTotal') }}
      </div>
      <div class="legacy-order-row-summary__value legacy-order-row-summary__total">
        {{ currency(rowTotal, order.currencyCode) }}
      </div>
    </div>

  </div>
</template>

<style scoped>

.warning {
  background-color: var(--color-warning);
  color: #000;
  border-radius: 4px;
  margin-left: 5px;
  padding: 0 3px;
}

</style>

<style>
.legacy-order-row-summary {
  display: grid;
  grid-column: middle / -2;
  grid-template-columns: subgrid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: right;
  padding: 5px;
}

.legacy-order-row-summary__item {
  display: contents;
  text-align: right;
}

.legacy-order-row-summary__label {
  white-space: nowrap;
  font-size: 12px;
  text-align: right;
  padding-left: 10px;
  color: hsl(var(--color-text-50));
}

.legacy-order-row-summary__value {
  grid-row: 2/3;
  padding-left: 10px;
  white-space: nowrap;
}

.legacy-order-row-summary__value span {
  white-space: nowrap;
}

.legacy-order-row-summary__value input {
  text-align: right;
  height: 22px;
}

.legacy-order-row-summary__total {
  font-weight: bold;
}

.legacy-order-row-summary__value--warning {
  color: var(--color-error);
}

.legacy-order-row-summary__pending-returns {
  font-size: 12px;
}

@container (max-width: 700px) {
  .legacy-order-row-summary {
    grid-column: 1 / -2;
  }
  .legacy-order-row-summary__item {
    display: block;
  }
  
  .legacy-order-row-summary {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: min-content min-content;
    justify-content: start;
    row-gap: 5px;
  }

  .legacy-order-row-summary .legacy-order-row-summary__item {
    grid-row: 1/2;
  }
  
  .legacy-order-row-summary .legacy-order-row-summary__item:nth-child(1),
  .legacy-order-row-summary .legacy-order-row-summary__item:nth-child(n+5) {
    grid-row: 2/3;
  }

  .legacy-order-row-summary .legacy-order-row-summary__item:nth-child(1),
  .legacy-order-row-summary .legacy-order-row-summary__item:nth-child(2) {
    grid-column: 1/2;
  }
  .legacy-order-row-summary .legacy-order-row-summary__item:nth-child(3),
  .legacy-order-row-summary .legacy-order-row-summary__item:nth-child(5) {
    grid-column: 2/3;
  }
}
</style>